import React, { createContext, useReducer, Dispatch } from "react";
import { DeliveryRules } from "../services/CourseService";
import {
  CourseDeliveryDTO,
  CourseSetDTO,
  AcademicYearDTO,
  CustomerDTO,
  SiteDTO,
  ProductDTO,
  TestAheadCourseDTO,
} from "@beable/roster-backend-client-v1";

enum CourseActionType {
  CustomerChanged,
  SiteChanged,
  ProductChanged,
  SchoolChanged,
  CourseChanged,
  StepChanged,
  AccessStepCompleted,
  DeliveryStepCompleted,
  CourseSetChanged,
  TestAheadCourseChanged,
  TestAheadCourseSaved,
}

enum ProvisionCourseStep {
  Access = "Set Course Access",
  Delivery = "Set Course Delivery",
  Provision = "Save Changes",
}

export type CourseAction =
  | {
      type: CourseActionType.CustomerChanged;
      customer: CustomerDTO | undefined;
    }
  | {
      type: CourseActionType.SiteChanged;
      site: SiteDTO | undefined;
    }
  | {
      type: CourseActionType.ProductChanged;
      product: ProductDTO | undefined;
    }
  | {
      type: CourseActionType.SchoolChanged;
      schoolId: string;
    }
  | {
      type: CourseActionType.CourseChanged;
      course: CourseDeliveryDTO;
    }
  | {
      type: CourseActionType.CourseSetChanged;
      courseSet: CourseSetDTO;
    }
  | {
      type: CourseActionType.TestAheadCourseChanged;
      testAheadCourse: TestAheadCourseDTO;
    }
  | {
      type: CourseActionType.TestAheadCourseSaved;
      testAheadCourseSaved: boolean;
    }
  | {
      type: CourseActionType.StepChanged;
      activeStep: ProvisionCourseStep;
    }
  | {
      type: CourseActionType.AccessStepCompleted;
      accessSelections: any[];
      accessChanged: boolean;
      currentAcademicYears: AcademicYearDTO[];
      futureAcademicYears: AcademicYearDTO[];
    }
  | {
      type: CourseActionType.DeliveryStepCompleted;
      deliverySelections: DeliveryRules;
      deliveryChanged: boolean;
    };

export type CourseState = {
  activeStep: ProvisionCourseStep;
  customer: CustomerDTO | undefined;
  site: SiteDTO | undefined;
  course: CourseDeliveryDTO | undefined;
  courseSet: CourseSetDTO | undefined;
  testAheadCourse: TestAheadCourseDTO | undefined;
  testAheadCourseSaved: boolean;
  schoolId: string | undefined;
  product: ProductDTO | undefined;
  accessSelections: any[] | undefined;
  accessChanged: boolean;
  deliverySelections: DeliveryRules | undefined;
  deliveryChanged: boolean;
  currentAcademicYears: AcademicYearDTO[] | [];
  futureAcademicYears: AcademicYearDTO[] | [];
};

type CourseDispatch = Dispatch<CourseAction>;

const CourseStateContext = createContext<CourseState | undefined>(undefined);
const CourseDispatchContext = createContext<CourseDispatch | undefined>(
  undefined
);

function createInitialState(): CourseState {
  return {
    activeStep: ProvisionCourseStep.Access,
    customer: undefined,
    site: undefined,
    course: undefined,
    courseSet: undefined,
    testAheadCourse: undefined,
    testAheadCourseSaved: false,
    schoolId: undefined,
    accessSelections: undefined,
    accessChanged: false,
    deliverySelections: undefined,
    deliveryChanged: false,
    currentAcademicYears: [],
    futureAcademicYears: [],
    product: undefined,
  };
}

function courseReducer(state: CourseState, action: CourseAction): CourseState {
  switch (action.type) {
    case CourseActionType.CustomerChanged: {
      return {
        ...state,
        customer: action.customer,
        site: undefined,
        schoolId: undefined,
        course: undefined,
        courseSet: undefined,
        testAheadCourse: undefined,
        testAheadCourseSaved: false,
        accessSelections: undefined,
        accessChanged: false,
        deliverySelections: undefined,
        deliveryChanged: false,
        currentAcademicYears: [],
        futureAcademicYears: [],
      };
    }
    case CourseActionType.SiteChanged: {
        return {
          ...state,
          site: action.site,
          schoolId: undefined,
          course: undefined,
          courseSet: undefined,
          testAheadCourse: undefined,
          testAheadCourseSaved: false,
          accessSelections: undefined,
          accessChanged: false,
          deliverySelections: undefined,
          deliveryChanged: false,
          currentAcademicYears: [],
          futureAcademicYears: [],
        };
    }
    case CourseActionType.ProductChanged: {
      if ( (state.product || action.product) && state.product !== action.product) {
        return {
          ...state,
          product: action.product,
          course: undefined,
          courseSet: undefined,
          testAheadCourse: undefined,
          testAheadCourseSaved: false,
          accessSelections: undefined,
          accessChanged: false,
          deliverySelections: undefined,
          deliveryChanged: false,
          currentAcademicYears: [],
          futureAcademicYears: [],
        };
      } else {
        return state;
      }
    }
    case CourseActionType.SchoolChanged: {
      if ( (state.schoolId || action.schoolId) && state.schoolId !== action.schoolId) {
        return {
          ...state,
          schoolId: action.schoolId,
          accessSelections: undefined,
          accessChanged: false,
          deliverySelections: undefined,
          deliveryChanged: false,
        };
      } else {
        return state;
      }
    }
    case CourseActionType.CourseChanged: {
      return {
        ...state,
        course: action.course,
        courseSet: undefined,
        testAheadCourse: undefined,
        testAheadCourseSaved: false,
        accessSelections: undefined,
        accessChanged: false,
        deliverySelections: undefined,
        deliveryChanged: false,
      };
    }
    case CourseActionType.CourseSetChanged: {
      return {
        ...state,
        courseSet: action.courseSet,
        course: undefined,
        testAheadCourse: undefined,
        testAheadCourseSaved: false,
        accessSelections: undefined,
        accessChanged: false,
        deliverySelections: undefined,
        deliveryChanged: false,
      };
    }
    case CourseActionType.TestAheadCourseChanged: {
      return {
        ...state,
        testAheadCourse: action.testAheadCourse,
        course: undefined,
        courseSet: undefined,
        accessSelections: undefined,
        accessChanged: false,
        deliverySelections: undefined,
        deliveryChanged: false,
      };
    }
    case CourseActionType.TestAheadCourseSaved: {
      return {
        ...state,
        testAheadCourseSaved: action.testAheadCourseSaved,
        course: undefined,
        courseSet: undefined,
        accessSelections: undefined,
        accessChanged: false,
        deliverySelections: undefined,
        deliveryChanged: false,
      };
    }
    case CourseActionType.StepChanged: {
      return {
        ...state,
        activeStep: action.activeStep,
      };
    }
    case CourseActionType.AccessStepCompleted: {
      return {
        ...state,
        activeStep: ProvisionCourseStep.Delivery,
        accessSelections: action.accessSelections,
        accessChanged: action.accessChanged,
        currentAcademicYears: action.currentAcademicYears,
        futureAcademicYears: action.futureAcademicYears,
      };
    }
    case CourseActionType.DeliveryStepCompleted: {
      return {
        ...state,
        activeStep: ProvisionCourseStep.Provision,
        deliverySelections: action.deliverySelections,
        deliveryChanged: action.deliveryChanged,
      };
    }
    default: {
      return state;
    }
  }
}

interface CourseProviderProps {
  children: React.ReactNode;
  initialState?: CourseState;
}

function CourseProvider({ children, initialState }: CourseProviderProps) {
  initialState =
    initialState !== undefined
      ? { ...createInitialState(), ...initialState }
      : createInitialState();
  const [courseState, courseDispatch] = useReducer(courseReducer, initialState);

  return (
    <CourseStateContext.Provider value={courseState}>
      <CourseDispatchContext.Provider value={courseDispatch}>
        {children}
      </CourseDispatchContext.Provider>
    </CourseStateContext.Provider>
  );
}

function useCourseState() {
  const context = React.useContext(CourseStateContext);

  if (context === undefined) {
    throw new Error(`useCourseState must be used within a CourseProvider`);
  }
  return context;
}

function useCourseDispatch() {
  const context = React.useContext(CourseDispatchContext);

  if (context === undefined) {
    throw new Error(`useCourseDispatch must be used within a CourseProvider`);
  }

  return context;
}

export {
  CourseProvider,
  CourseActionType,
  ProvisionCourseStep,
  useCourseState,
  useCourseDispatch,
};
